import { EVENT_STATUS } from '@hedepy/common/enums/EVENT'
import type { Event } from '@hedepy/common/schemas/modules/event/event'
import dayjs from 'dayjs'
import { pathOr } from 'ramda'

import createResultCheckingSelector from '@/legacy-src/store/memoize'

import { USER_TYPE } from '../../redux/UserRedux'
import type { ClientType, EventsByTherapistType } from '../../types/ClientStateType'
import type { TherapistType } from '../../types/TherapistStateType'
import type { CalendarUserType } from '../../types/UserStateType'
import { getFullName } from '../../utilities/name'
import type { ReduxState } from '../reducers'
import { therapistObjectSelector } from './ClientSelectors'
import { clientsObjectSelector } from './TherapistSelectors'

type UserMap = { [key: number]: ClientType | TherapistType }

type EventWithOtherUser = Event & { otherUser: CalendarUserType }

const userTypeFilter = (_: ReduxState, { userType }: { userType: number }) => userType

const eventsTherapistFilter = (state: ReduxState): Event[] => pathOr([], ['therapist', 'appointments'], state)

const eventsClientFilter = (state: ReduxState): EventsByTherapistType => pathOr({}, ['client', 'appointments'], state)

const getOtherUserDataToEventFilter = (
  userType: number,
  users: UserMap,
  event: Event,
): ClientType | TherapistType | undefined => {
  const id = userType === USER_TYPE.THERAPIST ? event.clientId : event.therapistId

  return id ? users[id] : undefined
}

const eventsPusher = (
  event: Event,
  reserved: {
    today: Event[]
    tomorrow: Event[]
    thisWeek: Event[]
    upcoming: Event[]
  },
  notReserved: Event[],
) => {
  if (event.status === EVENT_STATUS.RESERVED) {
    const now = dayjs()
    const dayjsStartDate = dayjs(event.startDate)
    const dayjsEndDate = dayjs(event.endDate)

    if (dayjsEndDate.isAfter(now) && !event.isCollision) {
      if (dayjsStartDate.isSame(now, 'day')) {
        reserved.today.unshift(event)
      } else if (dayjsStartDate.isSame(now.add(1, 'day'), 'day')) {
        reserved.tomorrow.unshift(event)
      } else if (dayjsStartDate.isSame(now, 'week')) {
        reserved.thisWeek.unshift(event)
      } else {
        reserved.upcoming.unshift(event)
      }
      return 1
    } else {
      // event has expired since being returned from backend
      // follow below
    }
  }
  if (![EVENT_STATUS.PRE_BOOKING, EVENT_STATUS.DENIED].includes(event.status) && !event.isCollision) {
    notReserved.push(event)
  }
  return 0
}

export const eventsByUserTypeSelector = createResultCheckingSelector(
  [userTypeFilter, eventsTherapistFilter, eventsClientFilter],
  (userType, therapistEvents, clientEvents) => {
    if (userType === USER_TYPE.THERAPIST) {
      return therapistEvents
    }
    return Object.values(clientEvents).reduce((acc, events) => acc.concat(events), [])
  },
)

export const eventsCollidingSelector = createResultCheckingSelector([eventsByUserTypeSelector], (clientEvents) =>
  clientEvents.find((event) => event.status === EVENT_STATUS.RESERVED && event.isCollision),
)

export const upcomingEventsSelector = createResultCheckingSelector([eventsByUserTypeSelector], (events) => {
  let reservedEventsCount = 0
  events.forEach((event) => {
    if (event.status === EVENT_STATUS.RESERVED) {
      reservedEventsCount++
    }
  })
  return reservedEventsCount
})

export const listOfAllMyUserSelector = createResultCheckingSelector(
  [userTypeFilter, clientsObjectSelector, therapistObjectSelector],
  (userType, clientsObject, therapistObject) => (userType === USER_TYPE.THERAPIST ? clientsObject : therapistObject),
)

export const reservedEventsSelector = createResultCheckingSelector(
  userTypeFilter,
  eventsByUserTypeSelector,
  listOfAllMyUserSelector,
  (userType, events, myUsers) => {
    const reserved: {
      today: EventWithOtherUser[]
      tomorrow: EventWithOtherUser[]
      thisWeek: EventWithOtherUser[]
      upcoming: EventWithOtherUser[]
    } = {
      today: [],
      tomorrow: [],
      thisWeek: [],
      upcoming: [],
    }
    let reservedEventsCount: number = 0

    const notReserved: Event[] = []

    events.forEach((event) => {
      const otherUserData = getOtherUserDataToEventFilter(userType, myUsers, event)
      if (otherUserData) {
        const eventWithUser = {
          ...event,
          otherUser: {
            firstName: otherUserData.firstName,
            verifiedCertificate: 'verifiedCertificate' in otherUserData ? otherUserData.verifiedCertificate : '',
            lastName: otherUserData.lastName,
            experience: 'experience' in otherUserData ? otherUserData.experience : '',
            recommendations: 'recommendations' in otherUserData ? otherUserData.recommendations : '',
            appointmentsCompleted: otherUserData.appointmentsCompleted,
            image: 'image' in otherUserData ? otherUserData.image : '',
          },
        }
        reservedEventsCount += eventsPusher(eventWithUser, reserved, notReserved)
      }
    })
    return {
      reservedEventsCount,
      reservedEvents: reserved,
      notReservedEvents: notReserved,
    }
  },
)

export const eventByIdSelector = createResultCheckingSelector(
  [eventsByUserTypeSelector, (_: ReduxState, { eventId }: { eventId: number }) => eventId],
  (events, eventId) => events.find((event) => event.id === eventId),
)

export const assignOtherUserDataToEventSelector = createResultCheckingSelector(
  [userTypeFilter, listOfAllMyUserSelector, eventByIdSelector],
  (userType, myUsers, event) => {
    if (!event) {
      return {
        event: {},
        otherUserData: { firstName: '', lastName: '', name: '' },
      }
    }

    const otherUserData = getOtherUserDataToEventFilter(userType, myUsers, event)
    return {
      event,
      otherUserData: {
        firstName: (otherUserData && otherUserData.firstName) || '',
        lastName: (otherUserData && otherUserData.lastName) || '',
        name: otherUserData ? getFullName({ person: otherUserData }) : '',
      },
    }
  },
)
